import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskedLine } from "../../lib/motion";
import { IMAGES, STATS } from "../../data/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, -6]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end pt-28 pb-16 overflow-hidden"
    >
      {/* ambient glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-[var(--accent)]/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 w-[30rem] h-[30rem] rounded-full bg-[#1b2b52]/40 blur-[120px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        {/* Left: kinetic type */}
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="overline mb-6"
          >
            Aryaki Finserv — Certified Financial Planning
          </motion.p>

          <h1 className="font-display font-medium text-[var(--text)] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[0.98] tracking-tight">
            <MaskedLine delay={0.15}>Wealth,</MaskedLine>
            <MaskedLine delay={0.28} className="text-gold italic">
              designed
            </MaskedLine>
            <MaskedLine delay={0.41}>around your life.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-8 max-w-xl text-base lg:text-lg text-white/70 leading-relaxed"
          >
            Mutual Funds, SIPs and Specialized Investment Funds — orchestrated by{" "}
            <span className="text-white">E.L.S. Ranjan</span> to fund your goals,
            protect your family and grow your wealth with quiet discipline.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => go("#contact")}
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[var(--accent)] text-[#0A0F1C] font-medium hover:bg-[#e6c65a] transition-colors duration-300"
            >
              Start your plan
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("#calculators")}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/20 text-white/85 hover:border-[var(--accent)] hover:text-gold transition-colors duration-300"
            >
              Try SIP calculator
            </button>
          </motion.div>
        </motion.div>

        {/* Right: clipped spotlight image with parallax + 2.5D */}
        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }}>
          <motion.div
            style={{ rotateY: rotate }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[420px] lg:h-[560px] rounded-t-[140px] overflow-hidden border border-white/10 gold-glow"
          >
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.abstract}
              alt="Wealth growth"
              className="absolute inset-0 w-full h-[130%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C] via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>

      {/* stats strip */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.35, duration: 0.9 }}
        className="absolute bottom-6 left-0 right-0"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="hairline mb-6 hidden lg:block" />
          <div className="grid grid-cols-3 gap-6">
            {STATS.map((s) => (
              <div key={s.label} data-testid={`stat-${s.label}`}>
                <div className="font-display text-3xl lg:text-4xl text-[var(--text)]">{s.value}</div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      <button
        onClick={() => go("#services")}
        className="absolute bottom-8 right-8 hidden lg:flex text-white/40 hover:text-gold transition-colors"
        aria-label="Scroll down"
      >
        <ArrowDown className="animate-bounce" />
      </button>
    </section>
  );
};
